import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_TOXIC } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantMergeBeatsWithSpacing,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/nekozilla.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/nekozilla.mp3',
  attribution:
    '"Nekozilla" by Different Heaven [NCS Release] - https://ncs.io/Nekozilla',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('nekozilla', variant),
    slug: buildVariantSlug('nekozilla', variant),
    title: buildVariantTitle('Nekozilla', variant),
    description:
      'Heavy electro at 128 BPM. Relentless kicks and rapid lead clusters - a hardest-tier monster sitting alongside Invincible.',
    bpm: onsets.bpm,
    difficulty: 5,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_TOXIC,
    audio,
  });

  // 60s @ 128 BPM ≈ 128 beats. The cut lands inside the second wave of the
  // track: an opening drop, a brief breakdown around beat 32, the heavy
  // main phrase from beat 48 onward, and a final-drop reprise to close.
  builder.section({ startBeat: 0, endBeat: 32, kind: 'drop' });
  builder.section({ startBeat: 32, endBeat: 48, kind: 'bridge' });
  builder.section({ startBeat: 48, endBeat: 96, kind: 'drop' });
  builder.section({ startBeat: 96, endBeat: 128, kind: 'drop' });

  // Drop 1: snare backbeat. Kicks are sparse this early in the cut and
  // the detector classifies anything <=0.25 intensity as 'lead' so most
  // of Nekozilla's lead onsets are sub-threshold synth bleed - rely on
  // strong snares for the rhythm and let chambers fill the gaps.
  //
  // Skip beat 0: the lead-in count-in's "GO!" overlay covers the centre
  // of the screen exactly as a beat-0 pad reaches the player, so it's
  // effectively unhittable. Starting at beat 1 lets the countdown clear
  // before the first pad arrives.
  const drop1 = variantSelectBeats(variant, onsets, {
    fromBeat: 1,
    untilBeat: 32,
    kinds: ['snare'],
    minIntensity: 0.3,
    minSpacing: 0.5,
  });

  // Break: snare backbeat - quieter than the drops but still anchored.
  // Chambers will fill the rest of this section.
  const bridgePads = variantSelectBeats(variant, onsets, {
    fromBeat: 32,
    untilBeat: 48,
    kinds: ['snare'],
    minIntensity: 0.3,
    minSpacing: 1,
  });

  // Main drop: kicks finally come out in force - 0.5-beat floor at 128 BPM
  // = 234ms, same target as Sky High's drop, but with heavier density.
  const drop2 = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 48,
      untilBeat: 96,
      kinds: ['kick'],
      minSpacing: 0.5,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 48,
      untilBeat: 96,
      kinds: ['snare'],
      minIntensity: 0.3,
      minSpacing: 0.5,
    }),
  );

  // Final drop reprise: matches main drop density. The very last beats
  // fade out so we cap at 126.
  const finalPads = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 96,
      untilBeat: 126,
      kinds: ['kick'],
      minSpacing: 0.5,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 96,
      untilBeat: 126,
      kinds: ['snare'],
      minIntensity: 0.3,
      minSpacing: 0.5,
    }),
  );

  const padBeats = mergeBeats(drop1, bridgePads, drop2, finalPads);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  // 128 BPM is mid-tempo for hardest tier; require longer gaps before
  // inserting a chamber so the relentless feel isn't broken.
  for (const c of findBurstChambers(padBeats, {
    fromBeat: 0,
    untilBeat: 126,
    minGapBeats: 8,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(128);
  return builder.build();
}

export const NEKOZILLA_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const NEKOZILLA_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(NEKOZILLA_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
